import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { InputNumberFormat } from "@react-input/number-format";
import { CheckCircle2, Edit3, XCircle } from "lucide-react";
import { useId, useState } from "react";

import { Input } from "../../../ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "../../../ui/input-group";
import { Label } from "../../../ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import type { LocationForForm, Price } from "./item-form-types";

const LocationPriceInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [price, setPrice] = useState(value);
  if (isEditing) {
    return (
      <div className="flex w-44 items-center">
        <InputGroup className="mr-1 h-9 rounded-md">
          <InputGroupAddon align="inline-start">
            <InputGroupText>$</InputGroupText>
          </InputGroupAddon>
          <InputNumberFormat
            className="body-sm py-2 pr-3"
            component={InputGroupInput}
            locales="en-US"
            maximumFractionDigits={2}
            minimumFractionDigits={2}
            onChange={(e) => setPrice(e.target.value)}
            value={price}
          />
        </InputGroup>
        <button
          className="p-2"
          onClick={() => {
            setIsEditing(false);
            onChange(price);
          }}
          type="button"
        >
          <CheckCircle2 className="text-success size-4" />
          <span className="sr-only">Save price</span>
        </button>
        <button
          className="p-2"
          onClick={() => {
            setIsEditing(false);
            setPrice(value);
          }}
          type="button"
        >
          <XCircle className="text-foreground size-4" />
          <span className="sr-only">Cancel</span>
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      ${value}
      <button
        className="p-1"
        onClick={() => {
          setIsEditing(true);
          setPrice(value);
        }}
        type="button"
      >
        <Edit3 className="text-foreground size-4" />
        <span className="sr-only">Edit price</span>
      </button>
    </div>
  );
};

const RadioGroupItem = ({ value, id }: { value: string; id: string }) => (
  <RadioGroupPrimitive.Item
    className="border-border data-[state=checked]:border-primary relative size-4 rounded-full border"
    id={id}
    value={value}
  >
    <RadioGroupPrimitive.Indicator className="bg-primary absolute inset-0 grid place-items-center rounded-full">
      <div className="bg-background size-1.5 rounded-full" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
);

export const PriceInput = ({
  value,
  onChange,
  locations,
}: {
  value: Price;
  onChange: (value: Price) => void;
  locations: LocationForForm[];
}) => {
  const id = useId();
  const onePrice =
    typeof value === "object"
      ? Math.max(
          ...Object.values(value).map((d) => Number.parseFloat(d)),
          0
        ).toString()
      : value;
  return (
    <fieldset className="space-y-5">
      <div className="space-y-1">
        <legend className="subtitle-sm text-foreground">Price</legend>
        <p className="body-sm text-muted-foreground">
          Set a single price for all locations using this menu or specify prices
          for each location individually.
        </p>
      </div>
      <RadioGroupPrimitive.Root
        onValueChange={(selectedValue: string) => {
          const newPrice = Number.parseFloat(onePrice).toFixed(2);
          onChange(
            selectedValue === "multiple"
              ? Object.fromEntries(
                  locations.map((location) => [location.id, newPrice])
                )
              : newPrice
          );
        }}
        value={typeof value === "object" ? "multiple" : "single"}
      >
        <div className="border-border grid grid-cols-[1rem_1fr] gap-2 border-b pb-5">
          <RadioGroupItem id={`${id}-single`} value="single" />
          <div className="space-y-5">
            <label htmlFor={`${id}-single`}>
              <p className="label text-foreground">Single price</p>
              <p className="body-sm text-muted-foreground">
                Use the same price for all locations
              </p>
            </label>
            {typeof value !== "object" && (
              <div className="space-y-2">
                <Label htmlFor={`${id}-single-price`}>Single price</Label>
                <div className="relative">
                  <span className="body-sm text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2">
                    $
                  </span>
                  <Input
                    className="body-sm px-3 py-2 pl-8"
                    id={`${id}-single-price`}
                    inputMode="decimal"
                    onChange={(e) => {
                      // Allow numbers with up to 2 decimal places
                      const inputValue = e.target.value.replace(/[^\d.]/g, "");
                      // Prevent multiple decimal points
                      const parts = inputValue.split(".");
                      if (parts.length > 2) {
                        return;
                      }
                      // Limit to reasonable price (up to 999999.99)
                      if (parts[0] && parts[0].length > 6) {
                        return;
                      }
                      if (parts[1] && parts[1].length > 2) {
                        return;
                      }
                      onChange(inputValue);
                    }}
                    placeholder="0.00"
                    type="text"
                    value={value}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-[1rem_1fr] gap-2 py-5">
          <RadioGroupItem id={`${id}-multiple`} value="multiple" />
          <div className="space-y-5">
            <label htmlFor={`${id}-multiple`}>
              <p className="label text-foreground">
                Different prices for each location
              </p>
              <p className="body-sm text-muted-foreground">
                Set individual prices for each location
              </p>
            </label>
            {typeof value === "object" && (
              <div className="border-border rounded-2xl border p-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Location</TableHead>
                      <TableHead className="w-48">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(value).map(([locationId, price]) => {
                      const location = locations.find(
                        (candidate) => candidate.id === locationId
                      );
                      if (!location) {
                        return null;
                      }
                      return (
                        <TableRow key={locationId}>
                          <TableCell>
                            <div className="label text-foreground flex items-center gap-3">
                              <img
                                alt={`${location.name} logo`}
                                className="size-6 rounded-full"
                                height={24}
                                src={
                                  location.logoUrl ??
                                  "/images/location-logo-placeholder.svg"
                                }
                                width={24}
                              />
                              {location.name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <LocationPriceInput
                              onChange={(newPrice) => {
                                onChange({
                                  ...value,
                                  [location.id]: newPrice,
                                });
                              }}
                              value={price}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </RadioGroupPrimitive.Root>
    </fieldset>
  );
};
